'use client';

import type { GeoJSONFeature, LngLat, MapMouseEvent } from 'mapbox-gl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layer, Source } from 'react-map-gl/mapbox';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import useMap from '@alltrails/maps/hooks/useMap';
import { updateCursor } from '@alltrails/maps/redux/reducer';
import { getLayerBeforeId, publicLandsId } from '@alltrails/maps/utils/layers';
import { useAppDispatch } from '../../redux/helpers';
import { PublicLandFeatureProperties } from '../../types/PublicLandFeatures';
import { ManagerCode } from '../../utils/constants';
import {
  BLMColor,
  CITYColor,
  CNTYColor,
  defaultColor,
  DODColor,
  FWSColor,
  NGOColor,
  NPSColor,
  REGColor,
  SDNRColor,
  SPRColor,
  TRIBColor,
  USACEColor,
  USBRColor,
  USFSColor
} from '../../utils/vectorOverlayColors';
import PublicLandsPopup from '../PublicLandsPopup';

const apiKey = process.env.NEXT_PUBLIC_API_KEY!;
const sourceId = 'arcgis-public-lands-overlay';
const publicLandsSourceLayer = 'publicLands';
const managerCodes: ManagerCode[] = ['BLM', 'USFS', 'FWS', 'NPS', 'DOD', 'USACE', 'USBR', 'SPR', 'SDNR', 'REG', 'CNTY', 'CITY', 'TRIB', 'NGO'];

function getFeatureProperties(feature?: GeoJSONFeature): PublicLandFeatureProperties | undefined {
  const properties = feature?.properties;
  if (!properties) {
    return undefined;
  }

  return properties as PublicLandFeatureProperties;
}

const PublicLandsOverlay = () => {
  const map = useMap();
  const publicBaseUrl = usePublicBaseUrl();
  const tileSource = `${publicBaseUrl}/wmts/overlay/publicLands/{z}/{x}/{y}?key=${apiKey}`;
  const dispatch = useAppDispatch();
  const hoveredFeatureId = useRef<string | number | null>(null);
  const [hoveredFeature, setHoveredFeature] = useState<{ properties: PublicLandFeatureProperties | undefined; lngLat: LngLat } | null>(null);
  const [selectedFeature, setSelectedFeature] = useState<{ properties: PublicLandFeatureProperties | undefined; lngLat: LngLat } | null>(null);
  const visibleManagers = useMemo(() => new Set(managerCodes), []);

  const handleMouseMove = useCallback(
    (event: MapMouseEvent) => {
      const feature = event.features?.[0];
      if (!feature?.properties) return;
      dispatch(updateCursor('pointer'));
      if (selectedFeature) return;
      const featureId = feature.properties?.ogc_fid || null;
      if (featureId !== hoveredFeatureId.current) {
        setHoveredFeature({ properties: getFeatureProperties(feature), lngLat: event.lngLat });
        hoveredFeatureId.current = featureId;
      }
    },
    [dispatch, selectedFeature]
  );

  const handleMouseLeave = useCallback(() => {
    hoveredFeatureId.current = null;
    if (!selectedFeature) {
      setHoveredFeature(null);
    }
    dispatch(updateCursor('default'));
  }, [dispatch, selectedFeature]);

  const handleClick = useCallback((event: MapMouseEvent) => {
    const feature = event.features?.[0];
    if (!feature?.properties) return;

    setSelectedFeature({ properties: getFeatureProperties(feature), lngLat: event.lngLat });
  }, []);

  const handleClosePopup = useCallback(() => {
    setSelectedFeature(null);
    setHoveredFeature(null);
  }, []);

  const handleMapClick = useCallback(
    (event: MapMouseEvent) => {
      if (!selectedFeature) return;

      const features = map?.queryRenderedFeatures(event.point, {
        layers: [publicLandsId]
      });

      if (!features?.length) {
        handleClosePopup();
      }
    },
    [selectedFeature, map, handleClosePopup]
  );

  useEffect(() => {
    if (map) {
      map.on('mousemove', publicLandsId, handleMouseMove);
      map.on('mouseleave', publicLandsId, handleMouseLeave);
      map.on('click', publicLandsId, handleClick);
      map.on('click', handleMapClick);
    }

    return () => {
      if (map) {
        map.off('mousemove', publicLandsId, handleMouseMove);
        map.off('mouseleave', publicLandsId, handleMouseLeave);
        map.off('click', publicLandsId, handleClick);
        map.off('click', handleMapClick);
      }
    };
  }, [map, handleMouseMove, handleMouseLeave, handleClick, handleMapClick]);

  const popupFeature = selectedFeature || hoveredFeature;

  const layerFilter = ['in', ['get', 'mang_name'], ['literal', Array.from(visibleManagers)]];

  return (
    <>
      <Source id={sourceId} type="vector" tiles={[tileSource]} promoteId="objectid">
        <Layer
          id={publicLandsId}
          beforeId={getLayerBeforeId(map, publicLandsId)}
          source-layer={publicLandsSourceLayer}
          type="fill"
          filter={layerFilter}
          paint={{
            'fill-color': [
              'match',
              ['get', 'mang_name'],
              'BLM',
              BLMColor,
              'USFS',
              USFSColor,
              'FWS',
              FWSColor,
              'NPS',
              NPSColor,
              'DOD',
              DODColor,
              'USACE',
              USACEColor,
              'USBR',
              USBRColor,
              'SPR',
              SPRColor,
              'SDNR',
              SDNRColor,
              'REG',
              REGColor,
              'CNTY',
              CNTYColor,
              'CITY',
              CITYColor,
              'TRIB',
              TRIBColor,
              'NGO',
              NGOColor,
              defaultColor // Default light green
            ],
            'fill-opacity': 0.5
          }}
          layout={{
            // Negate acres so smaller areas (lower acres) get higher sort keys and render on top
            'fill-sort-key': ['-', ['get', 'gis_acres']]
          }}
        />
      </Source>
      {popupFeature && <PublicLandsPopup selectedFeature={selectedFeature} handleClosePopup={handleClosePopup} popupFeature={popupFeature} />}
    </>
  );
};

export default PublicLandsOverlay;
